import type { AxiosError, AxiosResponse } from 'axios'
import type { Offer } from 'BrokerTsLib/src/dtos'
import { createContext, useContext } from 'react'
import type { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query'
import { Loading } from '../components/Loading'

import { useInsuranceParams } from '../context/InsuranceParamsProvider'
import { useGetOffer } from '../hooks/useOffer'

interface ISelectedQuoteContext {
  offer?: Offer
  isLoading: boolean
  refetch: <TPageData>(options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined) => Promise<QueryObserverResult<AxiosResponse<Offer, any>, any>>
  error?: AxiosError<unknown>
}

const SelectedQuoteContext = createContext<ISelectedQuoteContext>({} as ISelectedQuoteContext)

export const SelectedQuoteProvider = (props: React.PropsWithChildren) => {
  const { insuranceParams, isLoadingData: isLoadingInsuranceParams } = useInsuranceParams()
  const { data: dataOffer, isLoading, refetch, error } = useGetOffer(insuranceParams!.generalParams.selectedOfferId!, {
    enabled: !!insuranceParams?.generalParams.selectedOfferId,
    cacheTime: 0,
  })

  return (
    <SelectedQuoteContext.Provider value={{
      offer: dataOffer?.data,
      isLoading: isLoading || isLoadingInsuranceParams,
      refetch: refetch,
      error: error ?? undefined
    }}>
      {!isLoading && props.children}
      {isLoading && <Loading large={true} />}
    </SelectedQuoteContext.Provider>
  )
}

export const useSelectedQuote = () => {
  const context = useContext(SelectedQuoteContext)

  if (!context || context === undefined) {
    throw new Error('useSelectedQuote must be used within a SelectedQuoteProvider')
  }

  return context
}